/**
 * Analytics event type definitions — provider-agnostic.
 *
 * Pure event-shape types: enums, metadata interfaces, and unions used by
 * `trackingEvents.ts` and consumers throughout the app. No provider coupling.
 */

import { NotificationPermissionState, UserMode } from "@types";
import { ReferralCodeSource } from "@utils/referral";
import { CsChannel } from "./channel";

import type { RedirectSurface } from "./constants";

// ===== ENUMS =====

export enum AuthSource {
  MEMBER_PROTECTOR = "member_protector",
  GAME_ENTRY = "game_entry",
  BOTTOM_NAVIGATION_CENTER_BUTTON = "bottom_navigation_center_button",
  BOTTOM_NAVIGATION_PROFILE_BUTTON = "bottom_navigation_profile_button",
  BOTTOM_NAVIGATION_WITHDRAW_BUTTON = "bottom_navigation_withdraw_button",
  SHARE_BUTTON = "share_button",
  DAILY_REWARD_PAGE_CLAIM_BUTTON = "daily_reward_page_claim_button",
  VIP_BUTTON = "vip_button",
  SLOTHACK_BUTTON = "slothack_button",
  PLINKO_GAME_PLAY_BUTTON = "plinko_game_play_button",
  REFERRAL_PAGE_GET_STARTED_BUTTON = "referral_page_get_started_button",
  PAGE_HEADER_REGISTER_BUTTON = "page_header_register_button",
  CASHBACK_PAGE_CLAIM_BUTTON = "cashback_page_claim_button",
  MINI_PROMOTION_REGISTER_BANNER = "mini_promotion_register_banner",
  MINI_PROMOTION_DEPOSIT_BANNER = "mini_promotion_deposit_banner",
  MINI_PROMOTION_GAME_BANNER = "mini_promotion_game_banner",
  PLINKO_GAME_RECORDS_BUTTON = "plinko_game_records_button",
  PLINKO_GAME_GET_MORE_BALLS_BUTTON = "plinko_game_get_more_balls_button",
  PAGE_HEADER_LOGIN_BUTTON = "page_header_login_button",
  HOMEPAGE_REGISTER_BANNER = "homepage_register_banner",
  HOMEPAGE_DEPOSIT_BANNER = "homepage_deposit_banner",
  HOMEPAGE_GAME_BANNER = "homepage_game_banner",
}

export enum RegistrationMethod {
  USERNAME_PASSWORD = "username_password",
  PHONE_PIN = "phone_pin",
  GOOGLE_LOGIN = "google_login",
}

export enum UsernameSuggestionTrigger {
  USERNAME_TAKEN = "username_taken",
}

export enum UsernameSource {
  MANUAL = "manual",
  SUGGESTION = "suggestion",
}

export enum GoogleAuthSource {
  LOGIN = "login",
  REGISTER = "register",
}

export enum OTPMethod {
  SMS = "sms",
  EMAIL = "email",
  VOICE = "voice",
}

export enum RegistrationStep {
  CLICK_REGISTER = "click_register",
  FORM_SUBMIT = "form_submit",
  OTP_SENT = "otp_sent",
  OTP_VERIFY = "otp_verify",
  REGISTRATION_COMPLETE = "registration_complete",
}

export enum DuplicateField {
  USERNAME = "username",
  PHONE = "phone",
  EMAIL = "email",
}

export enum ErrorSeverity {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

// ===== DEPOSIT FLOW ENUMS =====

export enum DepositSource {
  PROFILE_PAGE = "profile_page",
  BOTTOM_NAVIGATION = "bottom_navigation",
  PROMOTION_BANNER = "promotion_banner",
  MEMBER_PROTECTOR = "member_protector",
  DIRECT_LINK = "direct_link",
  AUTO_REDIRECT = "auto_redirect",
  BALANCE_BADGE = "balance_badge",
  TRANSACTION_PAGE = "transaction_page",
  PROMOTION_INELIGIBLE_CTA = "promotion_ineligible_cta",
  HOMEPAGE_BANNER = "homepage_banner",
  FTD_GIFT_PROMPT = "ftd_gift_prompt",
}

export enum WithdrawalSource {
  PROFILE_PAGE = "profile_page",
  BOTTOM_NAVIGATION = "bottom_navigation",
}

export enum DepositMethod {
  BANK_TRANSFER = "bank_transfer",
  CREDIT_CARD = "credit_card",
  E_WALLET = "e_wallet",
  CRYPTO = "crypto",
  MOBILE_PAYMENT = "mobile_payment",
  AUTO_FAST = "auto_fast",
}

export enum DepositStep {
  CLICK_DEPOSIT = 1,
  SELECT_GROUP = 2,
  SELECT_METHOD = 3,
  SELECT_AMOUNT = 4,
  SUBMIT_TOP_UP = 5,
  SUBMIT_DEPOSIT = 6,
  DEPOSIT_COMPLETE = 7,
}

export enum NotificationInAppSource {
  PRIZES_PAGE = "prizes_page",
}

export enum NotificationAction {
  ALLOW = "allow",
  BLOCK = "block",
  CANCEL = "cancel",
  DISMISS = "dismiss",
}

export enum OTPSource {
  FORGOT_PASSWORD = "forgot_password",
  REGISTER = "register",
  LOGIN = "login",
}

export enum LanguageChangeSource {
  LANGUAGE_SELECT = "language_select",
  LANGUAGE_PAGE = "language_page",
}

export enum CsSource {
  HEADER = "header",
  LOGIN = "login",
  REGISTER = "register",
  OTP = "otp",
  DEPOSIT = "deposit",
}

// Phase 2 adds the remaining deposit steps and the withdrawal ones.
export type CsFlowStep = "method_list" | "pending_limit";

export interface CsClickMetadata {
  source: CsSource;
  // "group" when a chooser opens, the resolved channel when a single link opens directly.
  channel: CsChannel | "group";
  userMode: UserMode;
  flowStep?: CsFlowStep;
  targetUrl?: string;
}

export interface CsChannelMetadata {
  source: CsSource;
  channel: CsChannel;
  channelName: string;
  targetUrl: string;
}

export interface ContactCsEntryShownMetadata {
  source: CsSource;
  userMode: UserMode;
  flowStep?: CsFlowStep;
}

export interface HeaderShownMetadata {
  userMode: UserMode;
  buttonsShown: string[];
}

export interface FtdCountdownMetadata {
  variant: string;
  expireAt: string;
}

export interface OpenGetMoreBallsTabMetadata {
  /** null: not in the experiment */
  variant: string | null;
}

export enum PromotionType {
  DAILY_BONUS = "daily_bonus",
  DAILY_STREAK = "daily_streak",
  CASHBACK = "cashback",
  CASHBACK_MISSION = "cashback_mission",
  REFERRAL = "referral",
  REFER_BALLS = "refer_balls",
  MEGA_SHARE = "mega_share",
  SECRET_CODE_SHARER = "secret_code_sharer",
  SECRET_CODE_REDEEMER = "secret_code_redeemer",
  PLAY_AND_SPIN = "play_and_spin",
  VIP = "vip",
  SKINNER_BOX = "skinner_box",
  CS_COMPENSATION = "cs_compensation",
}

export const IframeDialogCloseReason = {
  MANUAL: "manual",
  GO_BACK_HISTORY: "go_back_history",
} as const;

export const DepositIframeCloseReason = {
  ...IframeDialogCloseReason,
  DEPOSIT_SUCCESS: "deposit_success",
  DEPOSIT_FAILED: "deposit_failed",
} as const;

export const DepositNativeCloseReason = {
  ...IframeDialogCloseReason,
  DEPOSIT_SUCCESS: "deposit_success",
  DEPOSIT_FAILED: "deposit_failed",
  GO_BACK_TOP_UP: "go_back_top_up",
  TIMEOUT: "timeout",
} as const;

// ===== REGISTRATION FLOW INTERFACES =====

export interface RegistrationClickMetadata {
  source: AuthSource;
  buttonText?: string;
  previousPage?: string;
  campaignId?: string;
  referralCode?: string;
}

export interface RegistrationFormFocusMetadata {
  method?: RegistrationMethod;
  pathname: string;
  fromPathname?: string;
  search?: string;
}

export interface RegistrationFormSubmitMetadata {
  method: RegistrationMethod;
  step: RegistrationStep;
  formValidationErrors?: string[];
  timeSpentOnForm?: number;
  retryCount?: number;
  autocompleteUsed?: boolean;
  referralCodeSource?: ReferralCodeSource;
}

export interface OTPSentMetadata {
  method: OTPMethod;
  step: RegistrationStep;
  phoneCountryCode?: string;
  retryCount?: number;
  deliveryTime?: number;
}

export interface OTPVerificationSuccessMetadata {
  method: OTPMethod;
  step: RegistrationStep;
  attemptsCount: number;
  timeToVerify?: number;
}

export interface OTPFailedAttemptMetadata {
  method: OTPMethod;
  step: RegistrationStep;
  attemptsCount: number;
  error: unknown;
  timeFromOTPSent?: number;
  source?: OTPSource;
}

export interface RegistrationCompleteMetadata {
  method: RegistrationMethod;
  step: RegistrationStep;
  hasReferralCode: boolean;
  referralCode?: string;
  totalTimeToComplete: number;
  stepsCompleted: RegistrationStep[];
  phoneCountryCode?: string;
  campaignId?: string;
}

export interface RegistrationDuplicateErrorMetadata {
  method: RegistrationMethod;
  duplicateField: DuplicateField;
  errorMessage: string;
  step: RegistrationStep;
  previousAttempts?: number;
}

export interface GoogleAuthClickMetadata {
  source: GoogleAuthSource;
}

// ===== DEPOSIT FLOW INTERFACES =====

export interface DepositClickMetadata {
  source: DepositSource;
  previousPage?: string;
  buttonText?: string;
  accountBalance?: number;
}

export interface DepositMethodGroupMetadata {
  source: DepositSource;
  step: DepositStep;
  groupId: string;
  groupName: string;
  availableMethods?: string[];
  timeOnSelection?: number;
}

export interface DepositMethodIndividualMetadata {
  source: DepositSource;
  step: DepositStep;
  methodId: string;
  methodName: string;
  methodType: DepositMethod;
  groupId?: string;
  isRecommended?: boolean;
  fees?: number;
  processingTime?: string;
}

export interface DepositAmountMetadata {
  source: DepositSource;
  step: DepositStep;
  amount: number;
  isQuickAmount: boolean;
  selectedMethod: string;
  bonusOffered?: number;
  currency: string;
}

export interface DepositSubmitMetadata {
  source: DepositSource;
  step: DepositStep;
  amount: number;
  method: string;
  methodId: string;
  methodKey: string;
  depositGroupId?: string;
  depositGroupName?: string;
  currency: string;
  estimatedProcessingTime?: string;
}

export interface AutoFastTriggeredMetadata {
  source: DepositSource;
  step: DepositStep;
  originalMethod?: string;
  autoSelectedMethod: string;
  amount: number;
  reason: string;
  timeSaved?: string;
}

export interface DepositSuccessMetadata {
  source: DepositSource;
  step: DepositStep;
  amount: number;
  method: DepositMethod;
  methodId: string;
  transactionId: string;
  currency: string;
  actualProcessingTime?: number;
  feesCharged?: number;
  bonusAwarded?: number;
  totalDepositTime?: number;
  isFirstDeposit?: boolean;
}

export interface DepositFailedMetadata {
  source: DepositSource;
  step: DepositStep;
  amount: number;
  method: DepositMethod;
  methodId: string;
  currency: string;
  errorCode: string;
  errorMessage: string;
  errorType: string;
  retryAttempt?: number;
  failurePoint?: string;
}

// Whether the channel was picked by Auto (Fast) or manually by the player.
export type ChannelSelectionMode = "auto" | "manual";

export interface DepositCreateResultOptions {
  source?: DepositSource;
  channelSelectionMode?: ChannelSelectionMode;
  currency?: string;
  methodId?: string;
  methodName?: string;
  orderRef?: string;
  depositGroupId?: string;
  depositGroupName?: string;
  methodKey?: string;
  amount?: number;
  providerKey?: string;
  paymentMethod?: string;
  payDataType?: string;
  iframeNewTabEligible?: boolean;
  errorCode?: string;
}

export interface CloseDepositIframeMetadata {
  reason: (typeof DepositIframeCloseReason)[keyof typeof DepositIframeCloseReason];
  orderRef: string;
}

export interface CloseDepositNativeMetadata {
  reason: (typeof DepositNativeCloseReason)[keyof typeof DepositNativeCloseReason];
  orderRef?: string;
}

// ===== PGW REDIRECT AUTO-CLOSE INTERFACES =====

export interface RedirectAutocloseResultMetadata {
  result: "success" | "blocked";
  surface: RedirectSurface;
  route: string;
  timeAliveMs: number;
}

// ===== NOTIFICATIONS INTERFACES =====

export interface NotificationInAppPromptShownMetadata {
  source: NotificationInAppSource;
}

export interface NotificationInAppPromptResponseMetadata {
  action: NotificationAction;
}

export interface NotificationBrowserPromptResponseMetadata {
  action: NotificationAction;
}

export interface NotificationSubscriptionStatusMetadata {
  status: NotificationPermissionState;
}

// ===== GENERIC INTERFACES =====

export interface EventMetadata {
  [key: string]: any;
}

// ===== TYPE UNIONS =====

export type RegistrationEventMetadata =
  | RegistrationClickMetadata
  | GoogleAuthClickMetadata
  | RegistrationFormSubmitMetadata
  | OTPSentMetadata
  | OTPVerificationSuccessMetadata
  | OTPFailedAttemptMetadata
  | RegistrationCompleteMetadata
  | RegistrationDuplicateErrorMetadata;

export type DepositEventMetadata =
  | DepositClickMetadata
  | DepositMethodGroupMetadata
  | DepositMethodIndividualMetadata
  | DepositAmountMetadata
  | DepositSubmitMetadata
  | AutoFastTriggeredMetadata
  | DepositSuccessMetadata
  | DepositFailedMetadata;

export type NotificationEventMetadata =
  | NotificationInAppPromptShownMetadata
  | NotificationInAppPromptResponseMetadata
  | NotificationBrowserPromptResponseMetadata
  | NotificationSubscriptionStatusMetadata;

export type AllEventMetadata =
  | RegistrationEventMetadata
  | DepositEventMetadata
  | NotificationEventMetadata
  | EventMetadata;

// ===== DEPOSIT EMPTY STATES =====

export interface DepositGroupsEmptyMetadata {
  groupsOriginalCount?: number;
  groupsWithActiveMethodsCount?: number;
  error?: string;
  path?: string;
  query?: string;
}

export enum SlipUploadFailReason {
  WRONG_FORMAT = "wrong_format",
  TOO_SMALL = "too_small",
  EMPTY = "empty",
  TOO_LARGE = "too_large",
  TIMEOUT = "timeout",
  NETWORK_ERROR = "network_error",
  SLIP_REJECTED = "slip_rejected",
  SERVER_ERROR = "server_error",
}

export interface SlipUploadAttemptMetadata {
  file: File;
  detectedType: string;
  attemptNumber: number;
  orderRef?: string;
}

export interface SlipUploadFailDetails {
  httpStatus?: string;
  errorMessage?: string;
  providerKey?: string;
  retry?: boolean;
}

export interface SlipUploadFailedMetadata extends SlipUploadAttemptMetadata, SlipUploadFailDetails {
  reason: SlipUploadFailReason;
}

export interface DepositMethodsEmptyMetadata {
  methodsOriginalCount?: number;
  methodsActiveCount?: number;
  simulateDeposit?: boolean;
  ignoreMintMethod?: boolean;
  mintAdded?: boolean;
  error?: string;
  path?: string;
  query?: string;
}

// ===== PROMOTION TRACKING INTERFACES =====

export interface ClickMiniBannerMetadata {
  bannerType: string;
  targetUrl?: string;
  isClaimable?: boolean;
  previousPage: string;
  position?: number;
}

export interface ClickHomeBannerMetadata {
  bannerType: string;
  targetUrl?: string;
  previousPage: string;
  position?: number;
}

export interface ClickPromotionPageMetadata {
  previousPage: string;
  hasClaimablePromotions: boolean;
  userMode: UserMode;
}

export interface ClickPromotionItemMetadata {
  promotionType: PromotionType;
  promotionPath: string;
  isClaimable: boolean;
  isEligible: boolean;
  isLocked?: boolean;
  sourcePage: string;
}

export interface CashbackMiniPromptShownMetadata {
  totalClaimable: number;
  /** Cashback earned since the last toast — the amount actually displayed (KZ-6720). */
  earnedAmount?: number;
  currency?: string;
  gameName?: string;
  isInGame: boolean;
}

// ===== GAMEPLAY =====

export interface GameExitParams {
  id?: string;
  name?: string;
  providerSourceRef?: string;
  providerGameCode?: string;
  category?: string | null;
  balanceChanged?: boolean;
  balanceDelta?: number;
  openSource?: string;
  openSourceCategory?: string;
  openPosition?: number;
}

// ===== PROMOTION USER JOURNEY (TK-815 / TK-903) =====
// `act:promotion_action` itself, plus `action`/`surface`, converged onto the
// canonical promotion contract in `events/promotion.ts` — this file now only keeps
// the fields with no canonical equivalent yet.

/**
 * `promotionType` values, spelled exactly as the TK-815 spec and the experiment's
 * exposure filter expect. Yes, this duplicates {@link PromotionType} with different
 * casing — flagged with UX Lab to converge later.
 */
export const PROMOTION_TYPE_LABEL = {
  REFER_AND_EARN: "Refer & Earn",
} as const;

/**
 * Screen the player tapped a share-link CTA from (TK-815's `screenContext`).
 * Screen-level, not button-level — Refer & Earn's "Copy Link" block and its
 * "Invite Friends" CTA both report `refer_earn`.
 */
export const PROMOTION_SCREEN_CONTEXT = {
  /** Refer & Earn → Overview: the "Copy Link" block and the "Invite Friends" claim CTA. */
  REFER_EARN: "refer_earn",
  /** Refer & Earn → How to Earn sub-page. */
  HOW_TO_EARN: "how_to_earn",
  /** Refer & Earn → Records: the "Copy Share Link" empty state, on either sub-tab. */
  RECORDS: "records",
  /** Refer & Earn → persistent page header Share icon (shown above all tabs). */
  REFER_EARN_HEADER: "refer_earn_header",
  /** Free Ball → Get More Balls page (header Share icon and the drawer). */
  FREE_BALL: "free_ball",
  /** Redeem Secret Code page header Share icon. */
  SECRET_CODE: "secret_code",
  /** Cashback page header Share icon. */
  CASHBACK: "cashback",
  /** Play & Spin page header Share icon. */
  PLAY_AND_SPIN: "play_and_spin",
  /** Daily Reward page header Share icon. */
  DAILY_REWARD: "daily_reward",
  /** Prizes page header Share icon. */
  PRIZES: "prizes",
  /** Refer & Earn nudge modal shown after a game exit. */
  NUDGE_MODAL: "nudge_modal",
} as const;

export type PromotionScreenContext = (typeof PROMOTION_SCREEN_CONTEXT)[keyof typeof PROMOTION_SCREEN_CONTEXT];

/**
 * Where a share went. `navigator.share` never tells us which app was picked, so
 * the only honest value for a native sheet is the sheet itself.
 */
export const SHARE_CHANNEL = {
  NATIVE_SHARE_SHEET: "native_share_sheet",
} as const;

export type ShareChannel = (typeof SHARE_CHANNEL)[keyof typeof SHARE_CHANNEL];

// ===== PWA INSTALL FUNNEL =====

export type PwaInstallSource =
  | "banner"
  | "profile"
  | "deposit_success_nudge"
  | "daily_streak"
  | "reward_toast"
  | "other";
export type PwaInstallMethod = "native" | "external";
export type PwaInstallSuccessTrigger = "android_appinstalled" | "first_standalone_open";

/**
 * Shared shape for every Surprise Me event.
 *
 * `surprise_session_id` and `game_position` stay snake_case on purpose: the
 * impact dashboard filters on these exact names. Same deliberate exception as
 * `experiment_variant` and `link_shape`.
 */
export interface SurpriseMeEventMetadata {
  /** Minted fresh on every tile tap. Identical for every event in one popup cycle. */
  surprise_session_id: string;
  /**
   * 1-based index of the game on screen. Opens at 1, Next adds 1, Back subtracts 1,
   * resets when the popup closes. Counts continuously across batches — the first
   * game of batch 2 is 6, never 1. Batching must not show up in this number.
   */
  game_position: number;
}
